import React, { useEffect, useState } from 'react';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Send,
  CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import LocationMap from '@/components/LocationMap';
import usePageContent from '@/hooks/usePageContent';

const ContactPage = () => {
  const { t: c } = usePageContent('kontakt');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    license_type: 'B',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'Kontakt | Jetzt anmelden | Fahrschule Rößner';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Deine Anfrage wurde erfolgreich gesendet!');
    } catch (error) {
      toast.error('Es gab einen Fehler. Bitte versuche es erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  const locations = [
    { value: 'frankfurt-schwanheim', label: 'Frankfurt Schwanheim' },
    { value: 'frankfurt-niederrad', label: 'Frankfurt Niederrad' },
    { value: 'offenbach', label: 'Offenbach am Main' },
    { value: 'bad-homburg', label: 'Bad Homburg' },
  ];

  const licenseTypes = [
    { value: 'B', label: 'Klasse B (PKW)' },
    { value: 'B197', label: 'Klasse B197 (Automatik)' },
    { value: 'BF17', label: 'Klasse BF17 (Begleitetes Fahren)' },
    { value: 'BE', label: 'Klasse BE (Anhänger)' },
    { value: 'A', label: 'Klasse A (Motorrad)' },
  ];

  if (submitted) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-lg mx-4">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-4">{c('success_title', 'Anfrage erfolgreich gesendet!')}</h1>
            <p className="text-slate-600 mb-6">
              {c('success_text', 'Vielen Dank für deine Anfrage. Wir melden uns schnellstmöglich bei dir.')}
            </p>
            <p className="text-slate-500 text-sm">
              Du kannst uns auch direkt anrufen: <a href="tel:+496920326059" className="text-blue-600 font-medium">069 20 32 60 59</a>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {c('hero_title', 'Jetzt anmelden')}
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            {c('hero_subtitle', 'Vereinbare dein kostenloses Beratungsgespräch oder melde dich direkt für deinen Führerschein an.')}
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6">{c('form_title', 'Kontaktformular')}</h2>
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">{c('label_name', 'Name *')}</Label>
                        <Input
                          id="name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="mt-1"
                          placeholder="Dein Name"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">{c('label_email', 'E-Mail *')}</Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="mt-1"
                          placeholder="deine@email.de"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone">{c('label_phone', 'Telefon *')}</Label>
                        <Input
                          id="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          className="mt-1"
                          placeholder="0123 456789"
                          data-testid="contact-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="location">{c('label_location', 'Bevorzugter Standort')}</Label>
                        <Select 
                          value={formData.location} 
                          onValueChange={(value) => setFormData({...formData, location: value})}
                        >
                          <SelectTrigger className="mt-1" data-testid="contact-location">
                            <SelectValue placeholder="Standort wählen" />
                          </SelectTrigger>
                          <SelectContent>
                            {locations.map((loc) => (
                              <SelectItem key={loc.value} value={loc.value}>
                                {loc.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="license_type">{c('label_license', 'Gewünschte Führerscheinklasse')}</Label>
                      <Select 
                        value={formData.license_type} 
                        onValueChange={(value) => setFormData({...formData, license_type: value})}
                      >
                        <SelectTrigger className="mt-1" data-testid="contact-license">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {licenseTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message">{c('label_message', 'Nachricht')}</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        className="mt-1"
                        rows={4}
                        placeholder="Deine Nachricht oder Fragen..."
                        data-testid="contact-message"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg"
                      disabled={submitting}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                      data-testid="contact-submit"
                    >
                      {submitting ? (
                        <span className="flex items-center gap-2">
                          <div className="spinner w-5 h-5"></div>
                          Wird gesendet...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send className="w-5 h-5" />
                          Anfrage senden
                        </span>
                      )}
                    </Button>

                    <p className="text-sm text-slate-500 text-center">
                      Mit dem Absenden stimmst du unserer <a href="/datenschutz" className="text-blue-600 underline">Datenschutzerklärung</a> zu.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-1">
              <div className="space-y-6">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">{c('sidebar_kontakt_title', 'Kontakt')}</h3>
                    <div className="space-y-4">
                      <a href={`tel:${c('sidebar_telefon', '069 20 32 60 59').replace(/\s/g, '')}`} className="flex items-center gap-3 text-slate-600 hover:text-blue-600 transition-colors">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Phone className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-sm text-slate-500">Telefon</div>
                          <div className="font-medium text-slate-900">{c('sidebar_telefon', '069 20 32 60 59')}</div>
                        </div>
                      </a>
                      <a href={`mailto:${c('sidebar_email', 'info@roessnerfahrschule.de')}`} className="flex items-center gap-3 text-slate-600 hover:text-blue-600 transition-colors">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Mail className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-sm text-slate-500">E-Mail</div>
                          <div className="font-medium text-slate-900">{c('sidebar_email', 'info@roessnerfahrschule.de')}</div>
                        </div>
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">{c('sidebar_standort_title', 'Hauptstandort')}</h3>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{c('sidebar_standort_name', 'Frankfurt Schwanheim')}</div>
                          <div className="text-slate-600" dangerouslySetInnerHTML={{ __html: c('sidebar_standort_adresse', 'Alt Schwanheim 29<br/>60529 Frankfurt am Main') }} />
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Clock className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">Öffnungszeiten</div>
                          <div className="text-slate-600" dangerouslySetInnerHTML={{ __html: c('sidebar_oeffnungszeiten', 'Mo-Fr: 09:00-18:00<br/>Sa: 10:00-14:00') }} />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-0 overflow-hidden rounded-xl">
                    <LocationMap 
                      slug="frankfurt-schwanheim" 
                      address="Alt Schwanheim 29, 60529 Frankfurt am Main" 
                      name="Frankfurt Schwanheim" 
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
